import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import downArrowSmall from "svg/downArrowSmall.svg";
import upArrowSmall from "svg/upArrowSmall.svg";
import dragDrop from "svg/dragDrop.svg";
import Pagination from "common/pagination";
import SelectInput from "common/selectInput";
import { Link } from "react-router-dom";
import MobileDataTable from "common/mobileDataTable";
import { paginationOptions } from "utils/common";
import { useSortableTable } from "../useSortableTable";
import search from "svg/search.svg";
import "./style.scss";

const ResizableDataTable = (props) => {
  const tableBodyNode = useRef();
  const {
    tableBody,
    tableHeader,
    countTitle,
    displaySearchFilter,
    searchFilterValue,
    handleSearchFilter,
    filteredTableList,
    component,
    expandedItems,
    renderMobileRows,
  } = props;
  const isSiteSearch = component === "siteRepoSearch";

  const [postsPerPage, setPostsPerPage] = useState(10);
  const [currentButton, setCurrentButton] = useState(1);
  const [arrOfCurrentButton, setArrOfCurrentButton] = useState([]);
  const tableElement = useRef(null);
  const [tableHeight, setTableHeight] = useState("auto");
  const [activeIndex, setActiveIndex] = useState(null);
  const minCellWidth = 120;

  const columns = tableHeader.map((item) => ({
    ...item,
    ref: useRef(),
  }));

  let filteredList;
  if (!filteredTableList) {
    filteredList = [];
  } else {
    filteredList = [...filteredTableList];
  }

  const { items, filteredItems, requestSort, sortConfig } = useSortableTable(tableBody, filteredList);
  const { dataKey, order } = sortConfig;
  const pageNode = useRef();
  const tdClass = "table-root-resizable__body__tr__td table-root__body__tr__td__text";
  const tableData =
    searchFilterValue && searchFilterValue !== "" && searchFilterValue.length > 0 ? filteredItems : items;

  /***********For Data table with pagination************/
  const numberOfPages = [];
  let currentData = displaySearchFilter ? [] : [...tableData];
  let howManyPages = 0;
  const indexOfLastPost = currentButton * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  currentData = tableData && tableData.slice(indexOfFirstPost, indexOfLastPost);
  if (tableData) {
    howManyPages = Math.ceil(tableData.length / postsPerPage);
  }
  for (let i = 1; i <= howManyPages; i++) {
    numberOfPages.push(i);
  }

  useEffect(() => {
    let tempNumberOfPages = [...arrOfCurrentButton];
    let dotsInitial = "...";
    let dotsLeft = "...";
    let dotsRight = "...";
    if (numberOfPages.length === 0 || numberOfPages.length === 1) {
      tempNumberOfPages = [1];
    } else if (numberOfPages.length === 2) {
      tempNumberOfPages = [1, 2];
    } else if (numberOfPages.length === 3) {
      tempNumberOfPages = [1, 2, 3];
    } else if (numberOfPages.length === 4) {
      tempNumberOfPages = [1, 2, 3, 4];
    } else {
      if (currentButton >= 1 && currentButton <= 3) {
        tempNumberOfPages = [1, 2, 3, dotsInitial, numberOfPages.length];
      } else if (currentButton === 4) {
        const sliced = numberOfPages.slice(0, 4);
        tempNumberOfPages = [...sliced, dotsInitial, numberOfPages.length];
      } else if (currentButton > 4 && currentButton < numberOfPages.length - 2) {
        const sliced1 = numberOfPages.slice(currentButton - 2, currentButton);
        const sliced2 = numberOfPages.slice(currentButton, currentButton + 1);
        tempNumberOfPages = [1, dotsLeft, ...sliced1, ...sliced2, dotsRight, numberOfPages.length];
      } else if (currentButton > numberOfPages.length - 3) {
        const sliced = numberOfPages.slice(numberOfPages.length - 4);
        tempNumberOfPages = [1, dotsLeft, ...sliced];
      } else if (currentButton === dotsInitial) {
        setCurrentButton(arrOfCurrentButton[arrOfCurrentButton.length - 3] + 1);
      } else if (currentButton === dotsRight) {
        setCurrentButton(arrOfCurrentButton[3] + 2);
      } else if (currentButton === dotsLeft) {
        setCurrentButton(arrOfCurrentButton[3] - 2);
      }
    }
    setArrOfCurrentButton(tempNumberOfPages);
  }, [currentButton, postsPerPage, tableBody, filteredTableList, searchFilterValue]);

  useEffect(() => {
    setTableHeight(tableElement.current.offsetHeight);
  }, []);

  const mouseDown = (index) => {
    setActiveIndex(index);
  };

  const mouseMove = useCallback(
    (e) => {
      const gridColumns = columns.map((col, i) => {
        if (i === activeIndex) {
          const width = e.clientX - col.ref.current.offsetLeft;

          if (width >= minCellWidth) {
            return `${width}px`;
          }
        }
        return `${col.ref.current.offsetWidth}px`;
      });

      tableElement.current.style.gridTemplateColumns = `${gridColumns.join(" ")}`;
    },
    [activeIndex, columns, minCellWidth],
  );

  const removeListeners = useCallback(() => {
    window.removeEventListener("mousemove", mouseMove);
    window.removeEventListener("mouseup", removeListeners);
  }, [mouseMove]);

  const mouseUp = useCallback(() => {
    setActiveIndex(null);
    removeListeners();
  }, [setActiveIndex, removeListeners]);

  useEffect(() => {
    if (activeIndex !== null) {
      window.addEventListener("mousemove", mouseMove);
      window.addEventListener("mouseup", mouseUp);
    }

    return () => {
      removeListeners();
    };
  }, [activeIndex, mouseMove, mouseUp, removeListeners]);

  const onSortingChange = (field) => {
    requestSort(field);
  };

  const handlePrevious = () => {
    setCurrentButton((prev) => (prev === 1 ? prev : prev - 1));
  };
  const handleNext = () => {
    setCurrentButton((prev) => (prev === numberOfPages.length ? prev : prev + 1));
  };

  const handleResultsPerPage = (e, count) => {
    const value = count?.description ? count.description : 10;
    setPostsPerPage(value);
    setCurrentButton(1);
  };

  const sectionOne = () => {
    const searchResultsCount =
      tableData.length > 0 ? (tableData.length < 10 ? "0" + tableData.length : tableData.length) : "0";
    return (
      <div className="section-one d-flex justify-content-between">
        <div className="d-flex section-one__results">
          <div className="section-one__results__heading">
            <span className="section-one__results__heading--text">{countTitle}:</span>
          </div>
          <div className="section-one__results__count">
            <span className="section-one__results__count--no">{searchResultsCount}</span>
          </div>
        </div>
        <div className="switch-toggle d-flex align-items-center">
          {displaySearchFilter && (
            <div className="section-one__filter">
              <input
                type="text"
                className="section-one__filter--input w-100"
                placeholder="Filter"
                value={searchFilterValue}
                onChange={handleSearchFilter}
              />
              <img src={search} alt={"search-icon"} className="section-one__filter--icon" />
            </div>
          )}
        </div>
      </div>
    );
  };

  const sectionTwo = (borderNone = false) => {
    return (
      <div className={`section-two ${borderNone ? "border-bottom-0" : ""}`}>
        <div className="d-flex justify-content-between section-two__root">
          <div className="d-flex search-pagination">
            <div className="pagination__results">
              <div className="d-flex pagination__results__wrapper">
                <div className="pagination__results__heading">Results per page</div>
                <SelectInput
                  options={paginationOptions}
                  inputRef={pageNode}
                  value={postsPerPage}
                  showEmptyValue={true}
                  type={"__pagination"}
                  handleSelectInput={(e, value) => handleResultsPerPage(e, value)}
                />
              </div>
            </div>
            <Pagination
              data={tableBody}
              numberOfPages={numberOfPages}
              setCurrentButton={setCurrentButton}
              currentButton={currentButton}
              arrOfCurrentButton={arrOfCurrentButton}
              handlePrevious={handlePrevious}
              handleNext={handleNext}
            />
          </div>
        </div>
      </div>
    );
  };

  const displayRowItem = (row, col) => {
    if (row && col && col.dataKey && row[col.dataKey]) {
      return row[col.dataKey];
    } else {
      return "";
    }
  };

  const renderTableRows = () => {
    return currentData.map((row, idx) => {
      const { siteId } = row;
      return (
        <tr key={siteId} className="table-root-resizable__body__tr displaySty">
          {columns.map((col, index) => {
            return (
              <>
                {col.name === "Site Name" && (
                  <td className={tdClass} key={`${siteId}-${index}`}>
                    <span>
                      <Link to={`/siteRepo/site/${siteId}`} className="sitename-link">
                        <span className="bodyDiv">{displayRowItem(row, col)}</span>
                      </Link>
                    </span>
                  </td>
                )}
                {col.name !== "Site Name" && (
                  <td className={tdClass} key={`${siteId}-${index}`}>
                    <span className="bodyDiv">{displayRowItem(row, col)}</span>
                  </td>
                )}
              </>
            );
          })}
        </tr>
      );
    });
  };

  return (
    <div className="search-table-wrapper">
      {!displaySearchFilter && <div className="search-table-title">{countTitle}</div>}
      <div className="container-fluid search-table">
        {sectionOne()}

        <MobileDataTable
          currentData={currentData}
          displaySearchFilter={displaySearchFilter}
          expandedItems={expandedItems}
          filteredList={filteredList}
          searchFilterValue={searchFilterValue}
          sorting={sortConfig}
          onSorting={(field) => requestSort(field)}
          tableData={tableData}
          isSiteSearch={isSiteSearch}
          renderMobileRows={renderMobileRows}
        />

        <div className="table-responsive resize-search__table">
          <table id="example" className="table table-root-resizable" ref={tableElement}>
            <thead className="table-root-resizable__header displaySty">
              <tr className="table-root-resizable__header__tr displaySty">
                {columns.map((col, i) => {
                  return (
                    <th
                      ref={col.ref}
                      key={col.id}
                      className={`table-root-resizable__header__tr__th table-root-resizable__body__tr__td__text`}
                      onClick={() => (col.sortable ? onSortingChange(col.dataKey) : null)}
                    >
                      <div className="d-flex gap-4">
                        <div className="bodyDiv">{col.name}</div>
                        {dataKey && dataKey === col.dataKey ? (
                          <img src={`${order === "asc" ? upArrowSmall : downArrowSmall}`} />
                        ) : col.sortable ? (
                          <div className="d-flex flex-column gap-2 pt-1">
                            <img className="sort-icon" src={upArrowSmall} />
                            <img className="sort-icon" src={downArrowSmall} />
                          </div>
                        ) : null}
                        <img className="dragDrop-icon resize-handle" src={dragDrop} onMouseDown={() => mouseDown(i)} />
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="table-root-resizable__body displaySty" ref={tableBodyNode}>
              {currentData.length > 0 ? (
                renderTableRows()
              ) : (
                <tr>
                  {" "}
                  {displaySearchFilter ? (
                    <td colSpan={12}>
                      <div className="d-flex flex-column text-center">
                        <div className="no-results--text">No results found</div>
                        <div className="no-results--info">
                          Try adjusting your search or filter to what you are looking for
                        </div>
                      </div>
                    </td>
                  ) : (
                    <td colSpan={12} className="no-results--text text-center">
                      Zero results were found based on your search criteria.
                    </td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {sectionTwo()}
      </div>
    </div>
  );
};

export default ResizableDataTable;
